/**
 * Web Audio engine (W1-14, A§8): `createAudioEngine` wires the context lifecycle, the gain graph, music decoding and
 * the two music paths, the transport, the visual clock, the look-ahead hitsound/metronome scheduler, the hitsound
 * bank, previews and the tap-along calibration.
 *
 * Graph: music (buffer sources or the media element) -> music gain; hitsounds -> hitsound gain; metronome clicks ->
 * metronome gain; all three -> master gain -> destination. Calibration clicks go straight to the master gain.
 *
 * Context lifecycle: the AudioContext ({latencyHint: 'interactive'}) is created on first need (decoding a song or a
 * skin sound works on a suspended context) and resumed by `ensureContext()` from a user gesture; until it runs, the
 * first gesture anywhere that carries user activation (key press, mouse press, touch or pen release, click; see
 * GESTURE_EVENTS) resumes it as well. `transport.play()` resumes it itself.
 *
 * Scheduling runs while playing on a 25 ms worker-timer tick plus every animation frame (`service()`).
 */

import type { AssetId } from '../core/model/types';
import type { Unsubscribe } from '../core/util/types';
import type {
  AudioEngine,
  CreateAudioEngine,
  EmissionSource,
  HitsoundBank,
  LatencySettings,
  MusicInfo,
  Volumes,
} from './types';
import { listenForTaps, startCalibration, type CalibrationSession } from './calibration';
import { VisualClock } from './clock';
import { readContainerInfo } from './containerInfo';
import { copyBytes, copyChannels, decodeAudio, sniffAudioMime } from './decode';
import { AudioEngineError, isAudioEngineError } from './errors';
import { SoundStore, VoicePool, type SkinSoundName, type SoundRef } from './hitsoundBank';
import { detectedLatencyMsOf, LATENCY_POLL_MS, readLatency, StableLatency } from './latency';
import { ClickSounds, Metronome, type BeatTimesFn } from './metronome';
import { BufferMusicOutput, SilentMusicOutput, type MusicOutput } from './music';
import { Scheduler, createFrameLoop, createTicker, type FrameLoop, type Ticker } from './scheduler';
import {
  ElementMusicOutput,
  STREAMING_MIN_DURATION_SEC,
  STREAMING_PROBE_MIN_BYTES,
  defaultTimers,
  probeDuration,
  setPreservesPitch,
  type MediaElementLike,
  type Timers,
} from './streaming';
import { TransportImpl } from './transport';

/** Polyphony of the metronome bus (clicks are short; a cap only guards against absurd tempos). */
const CLICK_VOICES = 16;
/** Volume at or below which hitsounds are not scheduled (Phira skips sfx at volume <= 0.01, R§5.4). */
const HITSOUND_AUDIBLE_MIN = 0.01;
/** Time constant of volume changes (avoids zipper noise). */
const VOLUME_SMOOTHING_SEC = 0.01;
const MAX_VOLUME = 4;
/** Per-type hitsound delays are clamped to +-500 ms (larger values are input mistakes, not device offsets). */
const MAX_HITSOUND_DELAY_MS = 500;

export interface ObjectUrls {
  create(blob: Blob): string;
  revoke(url: string): void;
}

/** Everything the engine takes from the browser (injectable for tests). */
export interface AudioEngineDeps {
  readonly createContext: () => AudioContext;
  /** performance.now() */
  readonly perfNow: () => number;
  /** Timestamp of the current animation frame (document.timeline.currentTime), in performance ms. */
  readonly frameTime: () => number;
  readonly createTicker: (onTick: () => void) => Ticker;
  readonly createFrameLoop: (onFrame: () => void) => FrameLoop;
  /** null when HTMLAudioElement is unavailable (no media-element path). */
  readonly createMediaElement: (() => MediaElementLike) | null;
  readonly connectMediaElement: (ctx: AudioContext, el: MediaElementLike) => AudioNode;
  readonly objectUrls: ObjectUrls | null;
  readonly timers: Timers;
  /** Tap input for calibration. */
  readonly listenTaps: (onTap: (perfMs: number) => void, onCancel: () => void) => Unsubscribe;
  /** Calls `cb` on user gestures (pointer, touch, click or key press) until unsubscribed. */
  readonly onGesture: (cb: () => void) => Unsubscribe;
  /** Max wait for media metadata when probing a large file's duration. */
  readonly probeTimeoutMs: number;
}

/**
 * Events that can carry user activation (HTML "activation-triggering input event": keydown, mousedown, pointerdown
 * for a mouse, pointerup and touchend for touch and pen) plus click, which older WebKit requires for audio unlock.
 * A touch or pen `pointerdown` does NOT grant activation, so a resume() there would be refused on touch devices.
 */
export const GESTURE_EVENTS = ['pointerdown', 'pointerup', 'mousedown', 'touchend', 'click', 'keydown'] as const;

function perfNowDefault(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

export function defaultAudioEngineDeps(): AudioEngineDeps {
  const g = globalThis as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
    Audio?: typeof Audio;
    document?: { readonly timeline?: { readonly currentTime?: unknown } };
    URL?: typeof URL;
    window?: EventTarget;
  };
  const urlApi = g.URL;
  const win = g.window;
  return {
    createContext: () => {
      const Ctor = g.AudioContext ?? g.webkitAudioContext;
      if (Ctor === undefined) throw new AudioEngineError('unsupported', 'no AudioContext');
      return new Ctor({ latencyHint: 'interactive' });
    },
    perfNow: perfNowDefault,
    frameTime: () => {
      const t = g.document?.timeline?.currentTime;
      return typeof t === 'number' ? t : perfNowDefault();
    },
    createTicker: (onTick) => createTicker(onTick),
    createFrameLoop: (onFrame) => createFrameLoop(onFrame),
    createMediaElement: g.Audio === undefined ? null : () => new Audio(),
    connectMediaElement: (ctx, el) => ctx.createMediaElementSource(el as unknown as HTMLMediaElement),
    objectUrls:
      urlApi === undefined || typeof urlApi.createObjectURL !== 'function'
        ? null
        : {
            create: (blob) => urlApi.createObjectURL(blob),
            revoke: (url) => {
              urlApi.revokeObjectURL(url);
            },
          },
    timers: defaultTimers,
    listenTaps: (onTap, onCancel) => (win === undefined ? () => undefined : listenForTaps(win, onTap, onCancel)),
    onGesture: (cb) => {
      if (win === undefined) return () => undefined;
      const opts = { capture: true, passive: true } as const;
      const handler = (): void => {
        cb();
      };
      for (const type of GESTURE_EVENTS) win.addEventListener(type, handler, opts);
      return () => {
        for (const type of GESTURE_EVENTS) win.removeEventListener(type, handler, opts);
      };
    },
    probeTimeoutMs: 4000,
  };
}

interface Graph {
  readonly master: GainNode;
  readonly music: GainNode;
  readonly hits: GainNode;
  readonly metronome: GainNode;
}

interface LoadedMusic {
  readonly asset: AssetId;
  readonly durationSec: number;
  readonly sampleRate: number;
  /** 0 when unknown (streaming-only song whose container header could not be read). */
  readonly channels: number;
  /** Decoded PCM (null for streaming-only songs). */
  readonly buffer: AudioBuffer | null;
  /** Encoded bytes for the media element (null without element support). */
  readonly blob: Blob | null;
  /** Longer than 20 min (or refused by decodeAudioData): always the media-element path. */
  readonly streamingOnly: boolean;
  element: MediaElementLike | null;
  url: string | null;
  elementNode: AudioNode | null;
}

const DEFAULT_VOLUMES: Volumes = { master: 1, music: 1, hitsounds: 1, metronome: 1 };
const DEFAULT_LATENCY: LatencySettings = { calibrationMs: 0, hitsoundDelayMs: { tapFlick: 0, drag: 0 } };

function finite(x: number, fallback: number): number {
  return Number.isFinite(x) ? x : fallback;
}

export class WebAudioEngine implements AudioEngine {
  private readonly deps: AudioEngineDeps;
  private ctx: AudioContext | null = null;
  private graph: Graph | null = null;
  private readonly clock: VisualClock;
  private readonly sounds = new SoundStore();
  private readonly hitPool = new VoicePool();
  private readonly clickPool = new VoicePool(CLICK_VOICES);
  private readonly metronome = new Metronome();
  private clickSounds: ClickSounds | null = null;
  readonly scheduler: Scheduler;
  readonly transport: TransportImpl;
  private loaded: LoadedMusic | null = null;
  /** Cached `music` snapshot (stable identity for useSyncExternalStore) and the song it describes. */
  private musicInfo: MusicInfo | null = null;
  private musicInfoOf: LoadedMusic | null = null;
  private bufferOut: BufferMusicOutput | null = null;
  private elementOut: ElementMusicOutput | null = null;
  private readonly silent = new SilentMusicOutput();
  private preservePitch = true;
  private volumes: Volumes = DEFAULT_VOLUMES;
  private latency: LatencySettings = DEFAULT_LATENCY;
  private readonly ticker: Ticker;
  private readonly frames: FrameLoop;
  private calibration: CalibrationSession | null = null;
  private loadToken = 0;
  private skinToken = 0;
  private disposed = false;
  private unlockOff: Unsubscribe | null = null;

  constructor(deps: AudioEngineDeps) {
    this.deps = deps;
    this.clock = new VisualClock(deps.perfNow);
    this.scheduler = new Scheduler({
      sounds: this.sounds,
      hitPool: this.hitPool,
      clickPool: this.clickPool,
      metronome: this.metronome,
      clicks: () => this.clickSounds,
    });
    this.transport = new TransportImpl({
      ensureRunning: () => this.ensureContext(),
      ctxNow: () => this.ctx?.currentTime ?? null,
      visualNow: () => this.visualCtxTime(this.deps.frameTime()),
      audibleNow: () => {
        const ctx = this.ctx;
        return ctx === null ? null : this.clock.rawAudible(ctx, this.deps.perfNow()) - this.calibrationSec();
      },
      outputFor: (rate) => this.outputFor(rate),
      changed: () => {
        this.reschedule();
      },
      playingChanged: (playing) => {
        if (playing) {
          this.ticker.start();
          this.frames.start();
        } else {
          this.ticker.stop();
          this.frames.stop();
        }
      },
    });
    this.ticker = deps.createTicker(() => {
      this.service();
    });
    this.frames = deps.createFrameLoop(() => {
      this.service();
    });
  }

  // ------------------------------------------------------------------ context

  get context(): AudioContext | null {
    return this.ctx;
  }

  /** The context, created (suspended) on first need. */
  private getContext(): AudioContext {
    if (this.disposed) throw new AudioEngineError('disposed');
    if (this.ctx !== null) return this.ctx;
    let ctx: AudioContext;
    try {
      ctx = this.deps.createContext();
    } catch (e) {
      if (isAudioEngineError(e)) throw e;
      throw new AudioEngineError('unsupported', e instanceof Error ? e.message : undefined, { cause: e });
    }
    const master = ctx.createGain();
    const music = ctx.createGain();
    const hits = ctx.createGain();
    const metronome = ctx.createGain();
    music.connect(master);
    hits.connect(master);
    metronome.connect(master);
    master.connect(ctx.destination);
    this.ctx = ctx;
    this.graph = { master, music, hits, metronome };
    this.applyVolumes(true);
    this.sounds.bind(ctx);
    this.clickSounds = new ClickSounds(ctx);
    this.clock.reset();
    // Keep the device-latency estimate fed (UX-073): a held median over live readings, not a one-shot sample.
    this.sampleLatency();
    ctx.addEventListener('statechange', () => {
      if (ctx.state === 'running') this.service();
    });
    if (ctx.state !== 'running') {
      this.unlockOff = this.deps.onGesture(() => {
        if (ctx.state === 'running' || ctx.state === 'closed') this.stopUnlock();
        else
          ctx.resume().then(
            () => {
              this.stopUnlock();
            },
            () => {
              // Refused (no activation on this event): the next gesture tries again.
            },
          );
      });
    }
    return ctx;
  }

  private stopUnlock(): void {
    this.unlockOff?.();
    this.unlockOff = null;
  }

  async ensureContext(): Promise<void> {
    const ctx = this.getContext();
    if (ctx.state !== 'running') {
      // resume() is called synchronously, inside the caller's user gesture.
      await ctx.resume();
    }
    this.stopUnlock();
  }

  /** Steady estimate: the browser's raw reading jitters by tens of ms (UX-073). */
  private readonly latencyEstimate = new StableLatency();
  private latencyHeld: number | null = null;
  private latencySampled = false;
  private latencyTimer: unknown = null;

  /**
   * One reading into the held estimate. Taken by the engine's own poll (and once on the first read), never by the
   * getter itself: rendering must not move the number (the getter used to sample per access, so every render
   * pulled a fresh jittery reading into the window).
   */
  private sampleLatency(): void {
    this.latencySampled = true;
    this.latencyHeld = this.latencyEstimate.read(detectedLatencyMsOf(readLatency(this.ctx)));
    if (this.disposed || this.ctx === null) return;
    if (this.latencyTimer !== null) this.deps.timers.clearTimeout(this.latencyTimer);
    this.latencyTimer = this.deps.timers.setTimeout(() => {
      this.latencyTimer = null;
      this.sampleLatency();
    }, LATENCY_POLL_MS);
  }

  /**
   * The held device-latency estimate (UX-073): median of the last readings, rounded, moving only when that median
   * moves by more than the hysteresis. Reading it takes no new sample.
   */
  get detectedLatencyMs(): number | null {
    if (!this.latencySampled) this.sampleLatency();
    return this.latencyHeld;
  }

  // ------------------------------------------------------------------ clocks and service

  private calibrationSec(): number {
    return finite(this.latency.calibrationMs, 0) / 1000;
  }

  /** Visual (audible, smoothed, calibrated) context time for the frame at `perfMs`. */
  private visualCtxTime(perfMs: number): number | null {
    const ctx = this.ctx;
    if (ctx === null) return null;
    return this.clock.audible(ctx, perfMs) - this.calibrationSec();
  }

  /** One scheduler pass: element clock, loop/end planning, song end, hitsound and metronome reconciliation. */
  service(): void {
    const ctx = this.ctx;
    if (ctx === null || this.disposed) return;
    this.transport.service(ctx.currentTime);
    this.reschedule();
  }

  private reschedule(): void {
    const ctx = this.ctx;
    const graph = this.graph;
    if (ctx === null || graph === null || this.disposed) return;
    this.scheduler.reconcile({
      ctx,
      now: ctx.currentTime,
      segments: this.transport.state.playing ? this.transport.segments : [],
      shift: this.transport.shift,
      hitBus: graph.hits,
      clickBus: graph.metronome,
    });
  }

  /** Buffers changed: drop pending voices so they are rescheduled with the new sounds. */
  private rescheduleAll(): void {
    const ctx = this.ctx;
    if (ctx === null) return;
    this.scheduler.cancelAll(ctx.currentTime);
    this.reschedule();
  }

  // ------------------------------------------------------------------ music

  /**
   * The loaded song. The object keeps its identity until the song or the streaming flag changes (both emit a
   * transport onChange), so it can serve as a useSyncExternalStore snapshot. `channels` is 0 when unknown (a
   * streaming-only song whose container header could not be read).
   */
  get music(): MusicInfo | null {
    const m = this.loaded;
    if (m === null) return null;
    const streaming = this.usesElement(this.transport.state.rate);
    const cached = this.musicInfo;
    if (cached !== null && this.musicInfoOf === m && cached.streaming === streaming) return cached;
    const info: MusicInfo = {
      asset: m.asset,
      durationSec: m.durationSec,
      sampleRate: m.sampleRate,
      channels: m.channels,
      streaming,
    };
    this.musicInfo = info;
    this.musicInfoOf = m;
    return info;
  }

  private wantsElement(rate: number): boolean {
    const m = this.loaded;
    return m !== null && (m.streamingOnly || (rate !== 1 && this.preservePitch));
  }

  private usesElement(rate: number): boolean {
    const m = this.loaded;
    if (m === null || !this.wantsElement(rate)) return false;
    return (
      m.element !== null || (m.blob !== null && this.deps.createMediaElement !== null && this.deps.objectUrls !== null)
    );
  }

  private outputFor(rate: number): MusicOutput {
    const m = this.loaded;
    const ctx = this.ctx;
    const graph = this.graph;
    if (m === null || ctx === null || graph === null) return this.silent;
    if (this.wantsElement(rate)) {
      const out = this.elementOutput(m, ctx, graph);
      if (out !== null) return out;
    }
    if (m.buffer === null) return this.silent;
    this.bufferOut ??= new BufferMusicOutput(ctx, m.buffer, graph.music);
    return this.bufferOut;
  }

  private elementOutput(m: LoadedMusic, ctx: AudioContext, graph: Graph): ElementMusicOutput | null {
    if (this.elementOut !== null) return this.elementOut;
    try {
      if (m.element === null) {
        const create = this.deps.createMediaElement;
        const urls = this.deps.objectUrls;
        if (create === null || urls === null || m.blob === null) return null;
        m.element = create();
        m.url = urls.create(m.blob);
        m.element.src = m.url;
      }
      const el = m.element;
      el.preload = 'auto';
      if (m.elementNode === null) {
        m.elementNode = this.deps.connectMediaElement(ctx, el);
        m.elementNode.connect(graph.music);
      }
      this.elementOut = new ElementMusicOutput({
        ctx,
        element: el,
        preservePitch: () => this.preservePitch,
        timers: this.deps.timers,
        onStall: () => {
          this.transport.stall();
        },
      });
      return this.elementOut;
    } catch {
      return null;
    }
  }

  private releaseMusic(): void {
    this.bufferOut?.dispose();
    this.bufferOut = null;
    this.elementOut?.dispose();
    this.elementOut = null;
    const m = this.loaded;
    this.loaded = null;
    this.musicInfo = null;
    this.musicInfoOf = null;
    if (m !== null) this.releaseElement(m.element, m.url, m.elementNode);
  }

  private releaseElement(el: MediaElementLike | null, url: string | null, node: AudioNode | null): void {
    node?.disconnect();
    if (el !== null) {
      el.pause();
      el.removeAttribute('src');
      el.load();
    }
    if (url !== null) this.deps.objectUrls?.revoke(url);
  }

  /** Drop the loaded song: playback stops, a pending load is superseded, the transport runs silent and unbounded. */
  unloadMusic(): void {
    if (this.disposed) return;
    this.loadToken++;
    if (this.transport.state.playing) this.transport.stop({ returnToStart: false });
    const had = this.loaded !== null;
    this.releaseMusic();
    this.transport.setDuration(null);
    if (had) this.transport.notify();
  }

  async loadMusic(asset: AssetId, bytes: ArrayBuffer): Promise<MusicInfo> {
    const token = ++this.loadToken;
    const ctx = this.getContext();
    if (this.transport.state.playing) this.transport.stop({ returnToStart: false });
    this.releaseMusic();
    this.transport.setDuration(null);

    const view = new Uint8Array(bytes);
    const canStream =
      this.deps.createMediaElement !== null && this.deps.objectUrls !== null && typeof Blob !== 'undefined';
    const blob = canStream ? new Blob([copyBytes(view)], { type: sniffAudioMime(view) }) : null;
    let element: MediaElementLike | null = null;
    let url: string | null = null;
    let probed: number | null | undefined;

    const probe = async (): Promise<number | null> => {
      const create = this.deps.createMediaElement;
      const urls = this.deps.objectUrls;
      if (blob === null || create === null || urls === null) return null;
      element = create();
      url = urls.create(blob);
      return probeDuration(element, url, this.deps.probeTimeoutMs, this.deps.timers);
    };
    const stale = (): boolean => token !== this.loadToken || this.disposed;
    const abandon = (): never => {
      this.releaseElement(element, url, null);
      throw new AudioEngineError(this.disposed ? 'disposed' : 'superseded');
    };

    if (view.byteLength >= STREAMING_PROBE_MIN_BYTES) {
      probed = await probe();
      if (stale()) abandon();
    }
    const streamingOnly = probed !== undefined && probed !== null && probed > STREAMING_MIN_DURATION_SEC;
    let buffer: AudioBuffer | null = null;
    let decodedChannels: number | null = null;
    let durationSec = probed ?? 0;
    if (!streamingOnly) {
      try {
        buffer = await decodeAudio(ctx, view);
        durationSec = buffer.duration;
        // Very long audio in a small file (below the probe threshold): keep the encoded bytes for the element and
        // let the PCM go rather than holding hundreds of MB.
        if (durationSec > STREAMING_MIN_DURATION_SEC && blob !== null) {
          decodedChannels = buffer.numberOfChannels;
          buffer = null;
        }
      } catch (e) {
        if (stale()) abandon();
        // decodeAudioData refused the file; the media element may still play it.
        if (probed === undefined) probed = await probe();
        if (stale()) abandon();
        if (probed === null) {
          this.releaseElement(element, url, null);
          throw e;
        }
        durationSec = probed;
      }
      if (stale()) abandon();
    }

    // Streaming-only songs have no AudioBuffer: channels and rate come from the container header.
    const header = buffer === null ? readContainerInfo(view) : null;
    this.loaded = {
      asset,
      durationSec,
      sampleRate: buffer?.sampleRate ?? header?.sampleRate ?? ctx.sampleRate,
      channels: buffer?.numberOfChannels ?? header?.channels ?? decodedChannels ?? 0,
      buffer,
      blob,
      streamingOnly: buffer === null,
      element,
      url,
      elementNode: null,
    };
    this.transport.setDuration(durationSec);
    this.transport.notify();
    const info = this.music;
    if (info === null) throw new AudioEngineError('superseded');
    return info;
  }

  musicChannels(): readonly Float32Array[] | null {
    const buffer = this.loaded?.buffer ?? null;
    return buffer === null ? null : copyChannels(buffer);
  }

  setOffset(offsetMs: number, infoOffsetSec: number, previewAudioDelayMs: number): void {
    const shift = finite(offsetMs, 0) / 1000 + finite(infoOffsetSec, 0) + finite(previewAudioDelayMs, 0) / 1000;
    this.transport.setShift(shift);
  }

  setPreservePitch(on: boolean): void {
    if (on === this.preservePitch) return;
    this.preservePitch = on;
    const el = this.loaded?.element ?? null;
    if (el !== null) setPreservesPitch(el, on && this.transport.state.rate !== 1);
    this.transport.refreshOutput();
    this.transport.notify();
  }

  // ------------------------------------------------------------------ hitsounds and metronome

  setEmissionSource(src: EmissionSource | null): void {
    this.scheduler.setSource(src);
    this.reschedule();
  }

  setHitsounds(bank: HitsoundBank): void {
    this.sounds.setExternal(bank);
    this.rescheduleAll();
  }

  async setSkinSounds(s: Readonly<Record<SkinSoundName, Uint8Array | null>>): Promise<void> {
    const token = ++this.skinToken;
    let ctx: AudioContext;
    try {
      ctx = this.getContext();
    } catch (e) {
      if (isAudioEngineError(e, 'unsupported')) return;
      throw e;
    }
    const names: readonly SkinSoundName[] = ['click', 'drag', 'flick'];
    const decoded = await Promise.all(
      names.map(async (name) => {
        const bytes = s[name];
        if (bytes === null) return null;
        try {
          return await decodeAudio(ctx, bytes);
        } catch {
          return null; // undecodable skin sound (e.g. Ogg on old Safari): procedural fallback
        }
      }),
    );
    if (token !== this.skinToken || this.disposed) return;
    names.forEach((name, i) => {
      this.sounds.setSkin(name, decoded[i] ?? null);
    });
    this.rescheduleAll();
  }

  async loadCustomHitsound(asset: AssetId, bytes: ArrayBuffer): Promise<void> {
    const ctx = this.getContext();
    const buffer = await decodeAudio(ctx, bytes);
    if (this.disposed) return;
    this.sounds.setCustom(asset, buffer);
    this.rescheduleAll();
  }

  playHitsound(sound: SoundRef): void {
    const ctx = this.ctx;
    const graph = this.graph;
    if (ctx === null || graph === null || this.disposed || ctx.state === 'closed') return;
    if (!(this.volumes.hitsounds > HITSOUND_AUDIBLE_MIN)) return;
    this.hitPool.acquire(ctx, this.sounds.resolve(sound), ctx.currentTime, graph.hits, ctx.currentTime, false);
  }

  preview(sound: 'click' | 'drag' | 'flick' | 'metronome'): void {
    let ctx: AudioContext;
    try {
      ctx = this.getContext();
    } catch {
      return;
    }
    const graph = this.graph;
    if (graph === null) return;
    if (ctx.state !== 'running') void ctx.resume().catch(() => undefined);
    const now = ctx.currentTime;
    if (sound === 'metronome') {
      const clicks = this.clickSounds;
      if (clicks !== null) this.clickPool.acquire(ctx, clicks.accent, now, graph.metronome, now, false);
      return;
    }
    this.hitPool.acquire(ctx, this.sounds.skinSound(sound), now, graph.hits, now, false);
  }

  setMetronome(on: boolean, beatTimes: BeatTimesFn): void {
    this.metronome.set(on, beatTimes);
    this.reschedule();
  }

  // ------------------------------------------------------------------ volumes and latency

  setVolumes(v: Volumes): void {
    const clamp = (x: number, prev: number): number => Math.min(MAX_VOLUME, Math.max(0, finite(x, prev)));
    const prev = this.volumes;
    this.volumes = {
      master: clamp(v.master, prev.master),
      music: clamp(v.music, prev.music),
      hitsounds: clamp(v.hitsounds, prev.hitsounds),
      metronome: clamp(v.metronome, prev.metronome),
    };
    this.applyVolumes(false);
    this.reschedule();
  }

  private applyVolumes(immediate: boolean): void {
    const ctx = this.ctx;
    const graph = this.graph;
    const v = this.volumes;
    this.scheduler.setHitsoundsAudible(v.hitsounds > HITSOUND_AUDIBLE_MIN);
    if (ctx === null || graph === null) return;
    const set = (p: AudioParam, value: number): void => {
      if (immediate) {
        p.value = value;
        return;
      }
      const now = ctx.currentTime;
      p.cancelScheduledValues(now);
      p.setTargetAtTime(value, now, VOLUME_SMOOTHING_SEC);
    };
    set(graph.master.gain, v.master);
    set(graph.music.gain, v.music);
    set(graph.hits.gain, v.hitsounds);
    set(graph.metronome.gain, v.metronome);
  }

  setLatency(l: LatencySettings): void {
    const delay = (ms: number): number =>
      Math.min(MAX_HITSOUND_DELAY_MS, Math.max(-MAX_HITSOUND_DELAY_MS, finite(ms, 0)));
    this.latency = {
      calibrationMs: finite(l.calibrationMs, 0),
      hitsoundDelayMs: { tapFlick: delay(l.hitsoundDelayMs.tapFlick), drag: delay(l.hitsoundDelayMs.drag) },
    };
    this.scheduler.setDelays({
      tapFlick: this.latency.hitsoundDelayMs.tapFlick / 1000,
      drag: this.latency.hitsoundDelayMs.drag / 1000,
    });
    this.reschedule();
  }

  // ------------------------------------------------------------------ calibration

  /**
   * Tap-along calibration (see calibration.ts). Pauses playback first. An optional AbortSignal (third argument)
   * cancels the session; so do Escape, a new session and dispose().
   */
  async calibrate(onTick: (i: number) => void, taps: number, signal?: AbortSignal): Promise<number> {
    this.calibration?.cancel();
    this.calibration = null;
    if (signal?.aborted === true) throw new AudioEngineError('calibrationCancelled');
    await this.ensureContext();
    const ctx = this.ctx;
    const graph = this.graph;
    const clicks = this.clickSounds;
    if (ctx === null || graph === null || clicks === null || this.disposed) throw new AudioEngineError('disposed');
    if (this.transport.state.playing) this.transport.stop({ returnToStart: false });
    const session = startCalibration(
      {
        ctx,
        bus: graph.master,
        click: clicks.accent,
        heardAt: (t) => this.clock.perfAt(ctx, t),
        perfNow: this.deps.perfNow,
        timers: this.deps.timers,
        listen: this.deps.listenTaps,
      },
      onTick,
      taps,
    );
    this.calibration = session;
    const onAbort = (): void => {
      session.cancel();
    };
    signal?.addEventListener('abort', onAbort);
    try {
      return await session.result;
    } finally {
      signal?.removeEventListener('abort', onAbort);
      if (this.calibration === session) this.calibration = null;
    }
  }

  // ------------------------------------------------------------------ lifecycle

  dispose(): void {
    if (this.disposed) return;
    this.calibration?.cancel();
    this.calibration = null;
    this.transport.dispose();
    this.disposed = true;
    this.loadToken++;
    this.skinToken++;
    this.ticker.dispose();
    this.frames.stop();
    this.stopUnlock();
    if (this.latencyTimer !== null) {
      this.deps.timers.clearTimeout(this.latencyTimer);
      this.latencyTimer = null;
    }
    const ctx = this.ctx;
    if (ctx !== null) {
      this.scheduler.cancelAll(ctx.currentTime);
      this.hitPool.stopAll(ctx.currentTime);
      this.clickPool.stopAll(ctx.currentTime);
    }
    this.releaseMusic();
    if (ctx !== null && ctx.state !== 'closed') void ctx.close().catch(() => undefined);
    this.ctx = null;
    this.graph = null;
  }
}

/** Engine with injectable browser dependencies (tests, embedding). */
export function createAudioEngineWith(overrides: Partial<AudioEngineDeps> = {}): WebAudioEngine {
  return new WebAudioEngine({ ...defaultAudioEngineDeps(), ...overrides });
}

export const createAudioEngine: CreateAudioEngine = () => createAudioEngineWith();
